// Touch drag-scrub for a horizontal strip: a finger dragged across it previews
// position-by-position and commits on release.
//
// Generic on purpose — it is handed an element and three callbacks and knows
// nothing about charts or calendars. Both use it.
//
// Every awkward detail here is load-bearing, and each one was a bug first:
//
//   - Taps are deliberately LEFT to whatever click handler the element already
//     has. Committing on pointerup instead would collide with the browser's
//     trailing synthetic click — and on the calendar that immediately un-pinned
//     the cell it had just pinned.
//   - Only a real HORIZONTAL drag takes over: a slop threshold, then pointer
//     capture. The strips carry `touch-action: pan-y`, so a vertical drag scrolls
//     the page instead (the browser then fires pointercancel, handled here).
//   - A finished drag squelches the one trailing click via `_squelchClick`, which
//     self-clears after 400ms in case no click arrives at all.
//   - Mouse pointers are ignored outright; they have their own listeners.

// The two flags this pattern hangs on a node: `_squelchClick` (set by a finished
// drag, swallowed by the next click) and `_wired` (an element whose listeners are
// already attached, so re-rendering cannot double-bind them).
export interface ScrubElement extends HTMLElement {
  _squelchClick?: boolean;
  _wired?: boolean;
  // Set by the caller when it has already attached a scrub to this node, so a
  // rebuild that reuses a strip does not stack a second one on it.
  _scrubbed?: boolean;
}

export interface ScrubHandlers {
  // Called continuously as the finger moves — preview only, do not commit.
  drag: (x: number, y: number) => void;
  // Called once on release, at the final position.
  end: (x: number, y: number) => void;
  // Called instead of `end` when the gesture is cancelled (e.g. the page scrolled).
  cancel?: () => void;
}

export function attachTouchScrub(el: ScrubElement, { drag, end, cancel }: ScrubHandlers): void {
  let startX = 0, startY = 0, dragging = false, pid: number | null = null;
  el.addEventListener('pointerdown', (e) => {
    if (e.pointerType === 'mouse') return;
    pid = e.pointerId;
    startX = e.clientX;
    startY = e.clientY;
    dragging = false;
  });
  el.addEventListener('pointermove', (e) => {
    if (pid == null || e.pointerId !== pid) return;
    if (!dragging) {
      const dx = Math.abs(e.clientX - startX);
      const dy = Math.abs(e.clientY - startY);
      if (dx < 6 || dx < dy) return; // vertical intent — let the page scroll
      dragging = true;
      try { el.setPointerCapture(pid!); } catch { /* pointer may already be gone */ }
    }
    drag(e.clientX, e.clientY);
  });
  const finish = (commit: boolean) => (e: PointerEvent) => {
    if (pid == null || e.pointerId !== pid) return;
    pid = null;
    if (!dragging) return;
    dragging = false;
    el._squelchClick = true;
    setTimeout(() => { el._squelchClick = false; }, 400);
    if (commit) end(e.clientX, e.clientY);
    else if (cancel) cancel();
  };
  el.addEventListener('pointerup', finish(true));
  el.addEventListener('pointercancel', finish(false));
}
